import { OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { RequireOnlyOne } from "@cervval.js/common/src/tools/object.tools";
import { LatLng, MapAddress, MapProfessional } from "@ehealth_tools/common-map/src/map.definitions";
import { Loader } from "@googlemaps/js-api-loader";
import { addressToPlainAddress, geocodeAddress, getGoogleAddress } from "../tools/map.tools";
import { _I18nMapComponent } from "./mapComponent.i18n";

export type PinStyle = {
    background: `#${string}`,
    borderColor: `#${string}`,
    glyphColor: `#${string}`
}
export interface MapOptions<P extends MapProfessional> {
    /**
     * List of professional to display on the map
     */
    professionals: P[];
    /**
     * Token of the map API
     */
    apiToken: string;
    /**
     * Id of the map for markers
     */
    mapID: string;
    /**
     * Zoom of the map
     */
    zoom?: number;
    /**
     * Callback function to click on marker
     */
    onClickMarkerCallback: (marker: google.maps.marker.AdvancedMarkerElement, professional: P) => void;
    /**
     * 
     * Callback function to click on the map
     */
    onClickMapCallback: () => void;
    /** Get Pin Style function of professional */
    getPinStyle?: (professional: P) => PinStyle | undefined;
    /** Method to save the localization obtained by google map */
    saveLocation?: (dto: MapAddress, localization: LatLng) => Promise<void>;

}

export const defaultMapOptions: MapOptions<MapProfessional> = {
    professionals: [],
    apiToken: "",
    mapID: "",
    onClickMarkerCallback: function (marker: google.maps.marker.AdvancedMarkerElement): void {
        throw new Error("Function not implemented.");
    },
    onClickMapCallback: function (): void {
        throw new Error("Function not implemented.");
    }
};

@CustomElement({
    name: "map-component",
    template: require("./mapComponent.html"),
    style: require("./mapComponent.scss"),
    i18n: require("./mapComponent.i18n").I18nMapComponent()
})
export class MapComponent<P extends MapProfessional, I18n extends _I18nMapComponent = _I18nMapComponent> extends WebComponent<I18n> {

    @TypedWebComponentOptions(defaultMapOptions)
    public readonly options!: WebComponentOptions<MapOptions<P>>;

    @TypedHtmlElements()
    public readonly components!: {
        map: HTMLDivElement
    };

    private _map: google.maps.Map | undefined; // Current Google MAP

    private _userPosition: google.maps.LatLng | null = null;

    constructor(options?: Partial<MapOptions<P>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Init component
     */
    public override init(): Promise<void> | void {

        // TODO Loader is meant to be a singleton element, having several instance of it could raised an error. Maybe add a service for map loading ?

        const loader = new Loader({
            apiKey: this.options.apiToken,
            version: "weekly",
            libraries: ["marker"]
        });

        loader.load().then(async () => {
            const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

            const mapDiv = this.components.map;
            // Create map
            const map = new Map(mapDiv, {
                center: { lat: 48.855, lng: 2.35 }, // Default map center is Paris (France) 
                mapId: this.options.mapID,
                zoom: this.options.zoom ?? 12,
                streetViewControl: false, // Disable Street View control
                mapTypeControl: false, // Disable map type control
                fullscreenControl: false, // Disable full screen control
                styles: [ // Disable all other markers
                    {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }]
                    },
                    {
                        featureType: "poi.business",
                        stylers: [{ visibility: "off" }]
                    },
                    {
                        featureType: "transit",
                        stylers: [{ visibility: "off" }]
                    }
                ]
            });

            this._map = map;

            map.addListener("click", () => {
                this.options.onClickMapCallback();
            });

            // User geolocation
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        this._userPosition = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
                        map.setCenter(this._userPosition);

                        // User marker
                        new google.maps.marker.AdvancedMarkerElement({
                            position: this._userPosition,
                            map,
                            title: this.i18n.positionTitle
                        });
                    },
                    () => {
                        alert(this.i18n.positionNotFind);
                    }
                );
            } else {
                alert(this.i18n.locationNotSupported);
            }

            // Add markers on the map
            this._addMarkersToMap(map);

            const locationButton = new CjsButton({
                label: this.i18n.positionTitle,
                icon: {
                    name: "fa-location-crosshairs"
                }
            });

            locationButton.classList.add("custom-map-control-button");

            map.controls[google.maps.ControlPosition.TOP_CENTER].push(locationButton);

            locationButton.addEventListener("click", () => {
                if (this._userPosition != null) {
                    map.setCenter(this._userPosition);
                }
            });
        }).catch(() => {

        });
    }

    /**
     * Add custom markers on the map
     * @param map Current map of the component
     */
    private async _addMarkersToMap(map: google.maps.Map): Promise<void> {

        const professionals = this.options.professionals;
        // For each professional, add a marker with their name + address          
        // List of all markers
        const markers: google.maps.marker.AdvancedMarkerElement[] = [];
        try {
            await Promise.all(professionals.map(async professional => {

                // Location of the marker retrieve with its address
                const location = await getGoogleAddress(professional.address, this.options.saveLocation);

                const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
                // Change the background color.
                const pinElement = new PinElement(this.options.getPinStyle?.(professional) ?? {
                    background: "#cbcbcb",
                    borderColor: "#414141",
                    glyphColor: "#414141"
                });
                // Create marker
                const marker = new AdvancedMarkerElement({
                    map: map,
                    position: location,
                    title: addressToPlainAddress(professional.address),
                    content: pinElement.element
                });
                marker.addListener("click", () => {
                    // pinElement.scale = 1.5;
                    // // Re-apply the content to update the scale
                    // marker.content = pinElement.element;

                    this.options.onClickMarkerCallback(marker, professional);
                    // Change center of the map to the marker location
                    map.panTo(location);
                });
                markers.push(marker);

            }));
        } catch (error) {
            console.error("Geocode error:", error);
        }
        // Display markers that are within the visible area of the map
        map.addListener("bounds_changed", () => {
            const bounds = map.getBounds();
            if (bounds) {
                markers.forEach(marker => {
                    if (bounds.contains(marker.position as google.maps.LatLng)) {
                        // Display marker
                        marker.map = map;
                    } else {
                        // Marker is not display
                        marker.map = null;
                    }
                });
            }
        });

    }


    /**
     * Set the center of the map to the address location
     */
    public async changeMapCenter(position: RequireOnlyOne<{ address: MapAddress, plainAddress: string }>): Promise<void> {
        const latLng = await (position.address ? getGoogleAddress(position.address) : geocodeAddress(position.plainAddress));
        // Retrieve the location
        if (this._map) {
            this._map.setCenter(latLng);
        }
    }

    /**
     * Refresh component
     */
    public override refresh(reason?: string): void {

    }
}